import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClipboardCheck, Search, CheckCircle2, Clock, XCircle, ShieldAlert, User, Mail, Calendar, DoorOpen, Undo2, Loader2, Camera, CameraOff } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

function useDebounced(value, delay = 300) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

function ReadyPill({ ready, waiverStatus, guardianStatus, isMinor }) {
  if (waiverStatus === 'declined' || guardianStatus === 'declined') {
    return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-red-600 text-white px-3 py-1.5 rounded-full">
      <XCircle size={14} /> DENIED
    </div>;
  }
  if (ready) {
    return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-emerald-600 text-white px-3 py-1.5 rounded-full">
      <CheckCircle2 size={14} /> READY
    </div>;
  }
  return <div className="inline-flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-amber-600 text-white px-3 py-1.5 rounded-full">
    <Clock size={14} /> {isMinor ? 'GUARDIAN PENDING' : 'PENDING'}
  </div>;
}

function NoPhotoToggle({ on, busy, onToggle, rowKey }) {
  return (
    <button type="button" onClick={onToggle} disabled={busy} data-testid={`no-photo-${rowKey}`} aria-pressed={on}
      title={on ? 'Guest asked for no photos. Click to allow photos again' : 'Mark this guest as NO PHOTOS'}
      className={`inline-flex items-center gap-1 font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded border transition-colors disabled:opacity-50 ${on
        ? 'bg-red-600 border-red-500 text-white shadow-[0_0_10px_rgba(220,38,38,0.6)]'
        : 'border-border text-muted-foreground hover:text-white hover:border-red-500/60'}`}>
      {busy ? <Loader2 size={10} className="animate-spin" /> : on ? <CameraOff size={10} /> : <Camera size={10} />}
      {on ? 'No photos' : 'Photos OK'}
    </button>
  );
}

function CheckedInBadge({ c, onUndo, busy }) {
  const time = new Date(c.checked_in_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'Asia/Tokyo' });
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`checkin-done-${c.waiver_id}`}>
      <span className="inline-flex items-center gap-1 font-heading text-[10px] tracking-widest uppercase bg-sky-950/60 border border-sky-500/50 text-sky-300 px-2 py-1 rounded-full">
        <DoorOpen size={12} /> Checked in · {c.room_name} · {time} · {c.employee_name}
      </span>
      <button type="button" onClick={onUndo} disabled={busy} data-testid={`checkin-undo-${c.waiver_id}`}
        className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase text-muted-foreground hover:text-white border border-border rounded-full px-2 py-1 disabled:opacity-50">
        {busy ? <Loader2 size={11} className="animate-spin" /> : <Undo2 size={11} />} Undo
      </button>
    </div>
  );
}

function CheckInPicker({ ctx, waiverId, onDone, onCancel }) {
  const staffList = ctx.on_shift.length ? ctx.on_shift.map(s => ({ id: s.employee_id, name: s.name, note: `${s.start}–${s.end}` })) : ctx.employees.map(e => ({ id: e.id, name: e.name }));
  const [room, setRoom] = useState('');
  const [staff, setStaff] = useState(staffList.length === 1 ? staffList[0].id : '');
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/admin/checkins`, { waiver_id: waiverId, room_id: room, employee_id: staff });
      toast.success(`Checked in to ${data.room_name} · added to ${data.employee_name}'s reservations`, { position: 'bottom-right' });
      onDone(data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Check-in failed', { position: 'bottom-right' }); }
    finally { setBusy(false); }
  };
  const pill = (on) => `px-3 py-1.5 rounded-full border text-xs font-heading tracking-widest uppercase transition-colors ${on ? 'bg-orange-500 border-orange-500 text-black' : 'border-border text-muted-foreground hover:text-white hover:border-orange-500/60'}`;
  return (
    <div className="mt-3 rounded-lg border border-orange-500/40 bg-black/50 p-3 space-y-3" data-testid={`checkin-picker-${waiverId}`}>
      <div>
        <div className="text-[10px] font-heading tracking-widest uppercase text-muted-foreground mb-1.5">Room</div>
        <div className="flex flex-wrap gap-2">
          {ctx.rooms.map(r => <button key={r.id} type="button" onClick={() => setRoom(r.id)} className={pill(room === r.id)} data-testid={`checkin-room-${r.id}`}>{r.name}</button>)}
        </div>
      </div>
      <div>
        <div className="text-[10px] font-heading tracking-widest uppercase text-muted-foreground mb-1.5">{ctx.on_shift.length ? "Who's checking them in (on shift today)" : 'Nobody is on today\'s shift list. Pick a staff member'}</div>
        <div className="flex flex-wrap gap-2">
          {staffList.map(s => <button key={s.id} type="button" onClick={() => setStaff(s.id)} className={pill(staff === s.id)} data-testid={`checkin-staff-${s.id}`}>{s.name}{s.note ? <span className="opacity-60 ml-1 normal-case">{s.note}</span> : null}</button>)}
        </div>
      </div>
      <div className="flex gap-2">
        <button type="button" onClick={submit} disabled={!room || !staff || busy} data-testid={`checkin-confirm-${waiverId}`}
          className="inline-flex items-center gap-1 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />} Confirm Check-In
        </button>
        <button type="button" onClick={onCancel} data-testid={`checkin-cancel-${waiverId}`} className="text-xs text-muted-foreground hover:text-white px-3">Cancel</button>
      </div>
    </div>
  );
}

export default function CheckInSearch() {
  const [ctx, setCtx] = useState(null);
  const [openPicker, setOpenPicker] = useState('');
  const [undoing, setUndoing] = useState('');
  const loadCtx = () => axios.get(`${API}/api/admin/checkin/context`).then(r => setCtx(r.data)).catch(() => {});
  useEffect(() => { loadCtx(); }, []);
  const checkinByWaiver = useMemo(() => Object.fromEntries((ctx?.checkins || []).map(c => [c.waiver_id, c])), [ctx]);
  const noPhoto = useMemo(() => new Set(ctx?.no_photo || []), [ctx]);
  const [photoBusy, setPhotoBusy] = useState('');
  const toggleNoPhoto = async (r) => {
    const next = !noPhoto.has(r.key);
    setPhotoBusy(r.key);
    try {
      await axios.put(`${API}/api/admin/no-photo`, { kind: r.kind, waiver_id: r.waiver_id, name: r.name, no_photo: next });
      setCtx(c => ({ ...c, no_photo: next ? [...(c.no_photo || []), r.key] : (c.no_photo || []).filter(k => k !== r.key) }));
      toast.success(next ? `${r.name} marked NO PHOTOS` : `${r.name} can be photographed again`, { position: 'bottom-right' });
    } catch { toast.error('Could not update photo preference', { position: 'bottom-right' }); }
    finally { setPhotoBusy(''); }
  };
  const undo = async (c) => {
    setUndoing(c.waiver_id);
    try {
      await axios.delete(`${API}/api/admin/checkins/${c.id}`);
      toast.success('Check-in undone and reservation removed', { position: 'bottom-right' });
      await loadCtx();
    } catch { toast.error('Could not undo', { position: 'bottom-right' }); }
    finally { setUndoing(''); }
  };

  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 250);
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load once on mount + whenever debounced query changes
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const params = debounced ? { q: debounced } : {};
        // Parallel: escape-room waivers + haunted-house waivers
        const [escR, hhR] = await Promise.all([
          axios.get(`${API}/api/admin/waivers`, { headers: authHeaders(), params }).catch(() => ({ data: { waivers: [] } })),
          axios.get(`${API}/api/admin/hh/waivers`, { headers: authHeaders(), params }).catch(() => ({ data: { waivers: [] } })),
        ]);
        const escaped = (escR.data.waivers || []).map(w => ({ ...w, _kind: 'escape' }));
        const haunted = (hhR.data.waivers || []).map(w => ({ ...w, _kind: 'haunted', signer_name: w.leader_name || w.signer_name }));
        if (!cancel) setWaivers([...escaped, ...haunted]);
      } catch {
        if (!cancel) setWaivers([]);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [debounced]);

  // Flatten waivers → one row per guest, with client-side name filter for extra precision
  const rows = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    const list = [];
    for (const w of waivers) {
      const guests = w.guests || [];
      for (const g of guests) {
        // Client-side name filter (server already loosely matched; this narrows for exact-name typing)
        if (q && !(g.name || '').toLowerCase().includes(q)
              && !(w.signer_name || '').toLowerCase().includes(q)
              && !(w.email || '').toLowerCase().includes(q)
              && !(w.waiver_id || '').toLowerCase().includes(q)) continue;
        const isMinor = !!g.is_minor;
        // Adults are ready as soon as the waiver is on file (any non-declined state).
        // Minors are only ready once THEIR guardian has approved.
        // Prior bug: adults inherited the whole-waiver "pending_guardian" status.
        const notDeclined = w.status !== 'declined' && g.guardian_status !== 'declined';
        const ready = notDeclined && (isMinor ? g.guardian_status === 'approved' : true);
        // Per-row status (independent of siblings)
        const rowStatus = (w.status === 'declined' || g.guardian_status === 'declined')
          ? 'declined'
          : isMinor
            ? (g.guardian_status === 'approved' ? 'signed' : (g.guardian_status || 'pending_guardian'))
            : 'signed';
        list.push({
          key: `${w._kind || 'escape'}:${w.waiver_id}:${g.name}`,
          kind: w._kind || 'escape',
          name: g.name,
          is_minor: isMinor,
          guardian_status: g.guardian_status,
          guardian_name: g.guardian_name,
          guardian_email: g.guardian_email,
          waiver_id: w.waiver_id,
          signer_name: w.signer_name,
          email: w.email,
          phone: w.phone,
          order_num: w.order_num,
          submitted_at: w.submitted_at,
          waiver_status: rowStatus,
          ready,
        });
      }
    }
    // Sort: READY first (green), then pending, then denied; within each group most-recent first
    const rank = { true: 0, false: 1 };
    return list.sort((a, b) => {
      // Denied last
      const aDenied = a.waiver_status === 'declined' || a.guardian_status === 'declined';
      const bDenied = b.waiver_status === 'declined' || b.guardian_status === 'declined';
      if (aDenied !== bDenied) return aDenied ? 1 : -1;
      if (a.ready !== b.ready) return rank[a.ready] - rank[b.ready];
      return new Date(b.submitted_at) - new Date(a.submitted_at);
    });
  }, [waivers, debounced]);

  const totals = useMemo(() => {
    return rows.reduce((acc, r) => {
      if (r.waiver_status === 'declined' || r.guardian_status === 'declined') acc.denied++;
      else if (r.ready) acc.ready++;
      else acc.pending++;
      return acc;
    }, { ready: 0, pending: 0, denied: 0 });
  }, [rows]);

  return (
    <div className="p-6 space-y-4 max-w-5xl mx-auto">
      <div className="flex items-center gap-3">
        <ClipboardCheck className="text-orange-400" size={22} />
        <h2 className="font-heading text-2xl tracking-widest uppercase">Check-In Search</h2>
      </div>
      <p className="text-xs text-muted-foreground">Front-desk look-up. Type a guest's name (or signer / email / waiver ID) — matches show up instantly. One row per guest.</p>

      {/* Big search box */}
      <div className="rounded-2xl border-2 border-orange-500/50 bg-black/60 p-2 flex items-center gap-3"
        style={{ boxShadow: '0 0 20px rgba(255,120,20,0.25)' }}>
        <Search size={20} className="text-orange-400 ml-2" />
        <input
          autoFocus
          data-testid="checkin-search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Type a guest name…"
          className="flex-1 bg-transparent outline-none text-lg py-2 pr-3 font-sans" />
        {query && (
          <button onClick={() => setQuery('')} data-testid="checkin-clear"
            className="px-3 text-xs text-muted-foreground hover:text-white">Clear</button>
        )}
      </div>

      {/* Live status chips */}
      <div className="flex gap-2 flex-wrap text-xs" data-testid="checkin-totals">
        <div className="font-heading tracking-widest uppercase bg-emerald-950/40 border border-emerald-500/40 text-emerald-400 px-3 py-1 rounded-full">Ready · {totals.ready}</div>
        <div className="font-heading tracking-widest uppercase bg-amber-950/40 border border-amber-500/40 text-amber-400 px-3 py-1 rounded-full">Pending · {totals.pending}</div>
        <div className="font-heading tracking-widest uppercase bg-red-950/40 border border-red-500/40 text-red-400 px-3 py-1 rounded-full">Denied · {totals.denied}</div>
      </div>

      {loading ? <p className="text-sm text-muted-foreground">Searching…</p> : (
        rows.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <User size={40} className="mx-auto text-neutral-700 mb-3" />
            <p className="font-heading text-sm tracking-widest uppercase">
              {debounced ? 'No matches' : 'Start typing a name to look up a guest'}
            </p>
          </div>
        ) : (
          <div className="space-y-2" data-testid="checkin-results">
            {rows.map(r => (
              <div key={r.key} data-testid={`checkin-row-${r.waiver_id}`}
                className="rounded-xl border border-border bg-card p-3 sm:p-4 flex items-center gap-3 hover:border-orange-500/50 transition-colors">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading text-lg tracking-wide truncate">{r.name}</span>
                    {r.is_minor && <span className="font-heading text-[9px] tracking-widest uppercase bg-yellow-950/60 border border-yellow-500/40 text-yellow-300 px-1.5 py-0.5 rounded">MINOR</span>}
                    <span className={`font-heading text-[9px] tracking-widest uppercase px-1.5 py-0.5 rounded ${
                      r.kind === 'haunted'
                        ? 'bg-red-950/60 border border-red-500/40 text-red-300'
                        : 'bg-orange-950/60 border border-orange-500/40 text-orange-300'
                    }`}>
                      {r.kind === 'haunted' ? 'HAUNTED HOUSE' : 'ESCAPE ROOM'}
                    </span>
                    {r.order_num && (
                      <span className="font-mono text-[10px] text-orange-300 bg-orange-950/40 border border-orange-500/40 px-1.5 rounded">
                        {r.order_num}
                      </span>
                    )}
                    {ctx && <NoPhotoToggle on={noPhoto.has(r.key)} busy={photoBusy === r.key} onToggle={() => toggleNoPhoto(r)} rowKey={r.key} />}
                  </div>
                  <div className="text-[11px] text-muted-foreground mt-0.5 flex items-center gap-3 flex-wrap">
                    <span className="inline-flex items-center gap-1"><User size={11} /> signed by {r.signer_name || '—'}</span>
                    <span className="inline-flex items-center gap-1"><Mail size={11} /> {r.email}</span>
                    <span className="inline-flex items-center gap-1"><Calendar size={11} /> {r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : '—'}</span>
                    <span className="font-mono text-[10px] text-neutral-500">{r.waiver_id}</span>
                  </div>
                  {r.is_minor && r.guardian_status === 'approved' && r.guardian_name && (
                    <div className="text-[10px] text-emerald-300 mt-0.5">
                      <ShieldAlert size={9} className="inline mr-1" />Guardian: {r.guardian_name}
                    </div>
                  )}
                  {r.kind === 'escape' && ctx && r.waiver_status !== 'declined' && (
                    checkinByWaiver[r.waiver_id]
                      ? <div className="mt-2"><CheckedInBadge c={checkinByWaiver[r.waiver_id]} busy={undoing === r.waiver_id} onUndo={() => undo(checkinByWaiver[r.waiver_id])} /></div>
                      : openPicker === r.key
                        ? <CheckInPicker ctx={ctx} waiverId={r.waiver_id} onCancel={() => setOpenPicker('')} onDone={() => { setOpenPicker(''); loadCtx(); }} />
                        : <button type="button" onClick={() => setOpenPicker(r.key)} data-testid={`checkin-btn-${r.waiver_id}`}
                            className="mt-2 inline-flex items-center gap-1 bg-orange-600 hover:bg-orange-500 text-white font-heading text-[10px] tracking-widest uppercase px-3 py-1.5 rounded-full">
                            <DoorOpen size={12} /> Check In
                          </button>
                  )}
                  {r.is_minor && r.guardian_status === 'pending' && (
                    <div className="text-[10px] text-amber-300 mt-0.5">
                      <ShieldAlert size={9} className="inline mr-1" />Guardian email sent to {r.guardian_email} · awaiting reply
                    </div>
                  )}
                </div>
                <ReadyPill ready={r.ready} waiverStatus={r.waiver_status} guardianStatus={r.guardian_status} isMinor={r.is_minor} />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
